import { collectionInputs } from '@ayumia/component-runtime';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ApiClient } from '../../../api/client';
import { Dialog, DialogActions } from '../../../design-system/Overlay';
import { Icon } from '../../../design-system/icons';
import { purposeFor, sha256, type FileEntry } from '../../../media/media-format';
import { mediaView } from '../../../media/media-views';
import { queryFolderSource, type Listing } from '../../../views/collection/folders/folder-source';
import { useImageLinks } from '../../../views/image-links';
import { refKey, useMediaRefLinks } from '../../../work/form-v2/media-links';

/**
 * Sélecteur de médias de l'éditeur v2 (ADR-106 addendum du 2026-10-21 §4, planche « EditeurV2-ChoisirImage ») :
 * « Mes images » (la médiathèque du tenant, ses dossiers, la recherche, les vignettes, le téléversement) et
 * « Communes » (la bibliothèque commune de la plateforme, en lecture seule), ou une icône de la liste fermée.
 * Le résultat est une référence de médiathèque (`media_ref`), jamais une URL (R3) : `file_id` pour une image
 * du tenant ; pour une image commune, sa clé stable `common_key` (la même dans toutes les bases), sinon son
 * identifiant. Les deux bibliothèques se lisent par `ListMedia` (`space`), sous l'autorisation de l'utilisateur ;
 * un téléversement va toujours dans « Mes images » (ADR-146 addendum (20)).
 */

export type MediaChoice = { image: { file_id: string } | { common_key: string } } | { icon: string };
type CommonEntry = FileEntry & { common_key?: string };

/** Icônes admises pour une option ou un formulaire (liste fermée `option_icon` du contrat 1.10.0). */
export const OPTION_ICONS = ['check', 'close', 'alert', 'help', 'minus', 'plus', 'eye', 'eye-off', 'thermometer', 'droplet', 'gauge', 'clock', 'calendar', 'package', 'boxes', 'truck', 'scale', 'trash', 'refresh', 'shield', 'tag', 'user', 'home', 'image', 'lock', 'location'] as const;

type Scope = 'mine' | 'common';

const SCOPE_LABEL: Record<Scope, string> = { mine: 'Mes images', common: 'Communes' };

/** Référence d'une image de la liste : la clé commune quand elle existe (stable d'une base à l'autre), sinon l'identifiant. */
export function choiceOf(file: CommonEntry): MediaChoice {
  return { image: file.common_key !== undefined ? { common_key: file.common_key } : { file_id: file.id } };
}

/** L'image de la liste désignée par un choix. */
function sameImage(choice: MediaChoice | undefined, file: CommonEntry): boolean {
  if (choice === undefined || !('image' in choice)) return false;
  const image = choice.image as { file_id?: string; common_key?: string };
  return image.file_id === file.id || (image.common_key !== undefined && image.common_key === file.common_key);
}

export function MediaPicker({ client, title, subtitle, allowIcon = true, current, onClose, onChoose }: {
  client: ApiClient;
  title: string;
  subtitle?: string;
  allowIcon?: boolean;
  current?: MediaChoice | undefined;
  onClose: () => void;
  onChoose: (choice: MediaChoice) => void;
}) {
  const spec = useMemo(() => collectionInputs(mediaView('images')).folders!, []);
  const sources = useMemo(() => {
    const declared = spec.listing_query!;
    // « Communes » : la même requête, espace de la bibliothèque commune (lecture seule).
    const common = { ...spec, listing_query: { ...declared, parameters: { ...(declared.parameters ?? {}), space: 'common' } } };
    return { mine: queryFolderSource<CommonEntry>(client, spec), common: queryFolderSource<CommonEntry>(client, common) };
  }, [client, spec]);
  const initialImage = current !== undefined && 'image' in current ? (current.image as { common_key?: string }) : undefined;
  const [scope, setScope] = useState<Scope>(initialImage?.common_key !== undefined ? 'common' : 'mine');
  const source = sources[scope];
  const [folder, setFolder] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [listing, setListing] = useState<Listing<CommonEntry> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [chosen, setChosen] = useState<MediaChoice | undefined>(current);
  const [uploading, setUploading] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let alive = true;
    setError(null);
    source
      .list({ folderId: folder, q: search.trim(), scope: 'everywhere', filters: {} })
      .then((result) => alive && setListing(result))
      .catch((failure: unknown) => alive && setError(failure instanceof Error ? failure.message : 'Médiathèque illisible.'));
    return () => {
      alive = false;
    };
  }, [source, folder, search, refresh]);
  const files = listing?.items ?? [];
  const thumbs = useImageLinks(client, files.map((file) => file.id), 'square_200');
  const chosenFile = files.find((file) => sameImage(chosen, file));
  // Aperçu d'un choix hors de la liste montrée (autre dossier, autre bibliothèque) : résolu par sa référence.
  const chosenRefs = useMemo(() => (chosen !== undefined && 'image' in chosen ? [chosen.image] : []), [chosen]);
  const chosenLinks = useMediaRefLinks(client, chosenRefs);
  const chosenKey = chosenRefs[0] === undefined ? undefined : refKey(chosenRefs[0]);
  const chosenUrl = chosenFile !== undefined ? thumbs.get(chosenFile.id) : chosenKey === undefined ? undefined : chosenLinks.get(chosenKey);
  const chosenCommon = chosen !== undefined && 'image' in chosen && ((chosen.image as { common_key?: string }).common_key !== undefined || chosenFile?.common_key !== undefined || (chosenFile !== undefined && scope === 'common'));
  const switchTo = (next: Scope): void => {
    if (next === scope) return;
    setScope(next);
    setFolder(null);
    setListing(null);
  };
  const upload = async (list: FileList | null): Promise<void> => {
    const file = list?.[0];
    if (file === undefined) return;
    setUploading(true);
    setError(null);
    try {
      const requested = await client.command<{ result: { file_id: string; upload: { url: string; method: string; headers?: Record<string, string> } } }>('RequestFileUpload', {
        payload: { file_name: file.name, media_type: file.type === '' ? 'application/octet-stream' : file.type, size_bytes: file.size, sha256: await sha256(file), purpose: purposeFor('images', null) },
      });
      await client.upload(requested.result.upload, file);
      const completed = await client.command<{ result: { lifecycle_state: string } }>('CompleteFileUpload', { payload: {}, target: { type: 'File', id: requested.result.file_id } });
      if (completed.result.lifecycle_state !== 'available') throw new Error('Image refusée par le contrôle des fichiers.');
      // Un téléversement va dans « Mes images » (la bibliothèque commune est en lecture seule).
      if (folder !== null && scope === 'mine') await client.command('MoveFiles', { payload: { file_ids: [requested.result.file_id], folder_id: folder } });
      setChosen({ image: { file_id: requested.result.file_id } });
      switchTo('mine');
      setRefresh((value) => value + 1);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Téléversement impossible.');
    } finally {
      setUploading(false);
    }
  };
  return (
    <Dialog
      open
      size="lg"
      title={title}
      {...(subtitle === undefined ? {} : { subtitle })}
      onClose={onClose}
      footer={<DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: 'Utiliser', disabled: chosen === undefined, onClick: () => chosen !== undefined && onChoose(chosen) }} />}
    >
      <div className="ay-v2-media">
        <aside className="ay-v2-media__side" aria-label="Bibliothèques">
          <div className="ay-studio__seg" role="radiogroup" aria-label="Bibliothèque">
            <button type="button" role="radio" aria-checked={scope === 'mine'} onClick={() => switchTo('mine')}>Mes images</button>
            <button type="button" role="radio" aria-checked={scope === 'common'} title="Partagées par tous les clients · lecture seule" onClick={() => switchTo('common')}>Communes</button>
          </div>
          <ul className="ay-v2-media__folders">
            <li>
              <button type="button" aria-current={folder === null ? 'true' : undefined} onClick={() => setFolder(null)}>
                <Icon name="image" size={14} /> Toutes
              </button>
            </li>
            {(listing?.folders ?? []).map((entry) => (
              <li key={entry.id}>
                <button type="button" aria-current={folder === entry.id ? 'true' : undefined} onClick={() => setFolder(entry.id)}>
                  <Icon name="folder" size={14} /> {entry.name}
                  {entry.item_count !== undefined && <span className="ay-studio__muted"> {entry.item_count}</span>}
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="ay-button ay-button--secondary" disabled={uploading} title={scope === 'common' ? 'Le téléversement va dans « Mes images »' : undefined} onClick={() => input.current?.click()}>
            <Icon name="upload" size={14} /> Téléverser
          </button>
          <input ref={input} type="file" accept="image/*" hidden onChange={(event) => void upload(event.target.files)} />
        </aside>
        <section className="ay-v2-media__main" aria-label="Images">
          <label className="ay-studio-search">
            <Icon name="search" size={14} />
            <span className="ay-visually-hidden">Rechercher une image</span>
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher" />
          </label>
          {listing !== null && listing.path.length > 0 && <p className="ay-studio__muted">{SCOPE_LABEL[scope]} › {listing.path.map((step) => step.name).join(' › ')} · {listing.total}</p>}
          {error !== null && <p className="ay-studio__refusal" role="alert">{error}</p>}
          <ul className="ay-v2-media__grid">
            {files.map((file) => {
              const selected = sameImage(chosen, file);
              return (
                <li key={file.id}>
                  <button type="button" aria-pressed={selected} className="ay-v2-media__tile" onClick={() => setChosen(choiceOf(file))} title={file.title}>
                    {thumbs.get(file.id) === undefined ? <Icon name="image" size={28} /> : <img src={thumbs.get(file.id)} alt="" />}
                    <span>{file.title}</span>
                  </button>
                </li>
              );
            })}
            {listing !== null && files.length === 0 && <li className="ay-studio-empty">Aucune image ici.</li>}
          </ul>
          {allowIcon && (
            <div className="ay-v2-media__icons" role="radiogroup" aria-label="Ou une icône">
              <span className="ay-field__label">Ou une icône :</span>
              {OPTION_ICONS.map((name) => (
                <button key={name} type="button" role="radio" aria-checked={chosen !== undefined && 'icon' in chosen && chosen.icon === name} aria-label={`Icône ${name}`} title={name} onClick={() => setChosen({ icon: name })}>
                  <Icon name={name} size={16} />
                </button>
              ))}
            </div>
          )}
        </section>
        <aside className="ay-v2-media__preview" aria-label="Aperçu">
          {chosen === undefined ? (
            <p className="ay-studio-empty">Aucune image choisie.</p>
          ) : 'icon' in chosen ? (
            <span className="ay-v2-media__big"><Icon name={chosen.icon} size={48} /></span>
          ) : (
            <>
              <span className="ay-v2-media__big">{chosenUrl === undefined ? <Icon name="image" size={48} /> : <img src={chosenUrl} alt="" />}</span>
              <strong>{chosenFile?.title ?? 'Image choisie'}</strong>
              <span className="ay-v2-pill">{chosenCommon ? 'Communes' : 'Mes images'}</span>
              {chosenCommon && <span className="ay-studio__muted" title="Partagée par tous les clients">Lecture seule</span>}
            </>
          )}
        </aside>
      </div>
    </Dialog>
  );
}
